/**
 * What a request row says about itself, for the dialogs that answer it: the
 * policy as one line, the items our team handed back to the office, and what
 * our team wrote when they did. Shared by the Provide details form
 * (InfoAskReplyModal), the estimate/claim results modal (RequestResultsModal)
 * and the breakdown Resubmit dialog — one reading of a row, not three.
 */
import { describeMember } from "../TreatmentPlans/lib/policyCompleteness";
import { formatDay } from "./outcomes";

const ORDINALS = { primary: "Primary", secondary: "Secondary", tertiary: "Tertiary", other: "Other" };

// The files the server takes with an office reply (views_insurance_requests
// CLAIM_FORM_ALLOWED_SUFFIXES / CLAIM_FORM_MAX_BYTES — the claim form's rules).
export const RESUBMIT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.heic";
export const RESUBMIT_MAX_BYTES = 20 * 1024 * 1024;

/** "Sun Life · Primary · Ada Byron (Self) · Member M1 · Group G-1". */
export function policyLine(policy) {
  if (!policy) return "";
  const parts = [
    policy.carrier_name,
    ORDINALS[policy.insurance_type] || policy.insurance_type,
    describeMember(policy),
    policy.member_id ? "Member " + policy.member_id : null,
    policy.contract_number ? "Group " + policy.contract_number : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

/** The items our team handed back to the office. */
export function waitingItems(row) {
  return (Array.isArray(row && row.requested_estimates) ? row.requested_estimates : []).filter(function (s) {
    return s.state === "waiting_on_office";
  });
}

/** "Claim 2 · $450.00 · Sep 3, 2026" — numbered as on the request's cards, so
 * the office can tell the items apart. */
export function itemTitle(item, meta, index, count) {
  const noun = meta.submissionNoun || "estimate";
  const parts = [noun.charAt(0).toUpperCase() + noun.slice(1) + (count > 1 ? " " + (index + 1) : "")];
  if (item.amount) parts.push("$" + item.amount);
  if (item.service_date) parts.push(formatDay(item.service_date));
  if (parts.length === 1 && !item.cda_itrans_number && !item.has_claim_form) parts.push("no details given");
  return parts.join(" · ");
}

/** The entry our team recorded when they handed this item back — the newest
 * with the item's outcome (results arrive newest first). It carries their note
 * (the server requires one), the day they contacted the carrier and, if any,
 * the carrier's response document. */
export function handBackEntry(item) {
  return (item && item.results ? item.results : []).find(function (r) {
    return r.outcome === item.outcome && r.note;
  }) || null;
}

/** What our team wrote when they handed this item back. */
export function askedFor(item) {
  const entry = handBackEntry(item);
  return entry ? entry.note : null;
}

/** The policy's own Details Needed flag, when it is what the office is
 * answering: the row's note is then the policy's ("the member id doesn't
 * match"), not an item's. */
export function policyAskOf(row) {
  if (!row || row.answer !== "missing_info") return null;
  const items = waitingItems(row);
  return items.some(function (s) { return s.outcome === "missing_info"; }) ? null : row.note;
}
